import React, { FC } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { AnalysisSummary } from "../../hooks";

import { Card, CardContent } from "@components/base";

interface PaygapStatisticalIndicatorsProps {
  analysisSummary: AnalysisSummary;
}

const PaygapStatisticalIndicators: FC<PaygapStatisticalIndicatorsProps> = ({
  analysisSummary,
}) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t("paygap.statistical.indicators.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("paygap.statistical.indicators.subtitle")}
        </Typography>

        {/* Statistical Indicators Table */}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>{t("paygap.statistical.indicators.indicator")}</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{t("paygap.statistical.indicators.value")}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  {t("paygap.statistical.indicators.discriminationCoefficient")}
                </TableCell>
                <TableCell align="right">
                  {analysisSummary.discriminationCoefficient.toFixed(4)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  {t("paygap.statistical.indicators.standardErrorCoefficient")}
                </TableCell>
                <TableCell align="right">
                  {analysisSummary.standardErrorCoefficient.toFixed(4)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  {t("paygap.statistical.indicators.totalDataRecords")}
                </TableCell>
                <TableCell align="right">
                  {analysisSummary.totalDataRecords.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  {t("paygap.statistical.indicators.employeesAnalysisContracts")}
                </TableCell>
                <TableCell align="right">
                  {analysisSummary.employeesAnalysisContracts.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  {t("paygap.statistical.indicators.notInAnalysis")}
                </TableCell>
                <TableCell align="right">
                  {analysisSummary.notInAnalysis.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  {t("paygap.statistical.indicators.employeesAnalysisValid")}
                </TableCell>
                <TableCell align="right">
                  {analysisSummary.employeesAnalysisValid.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t("paygap.statistical.indicators.women")}</TableCell>
                <TableCell align="right">
                  {analysisSummary.women.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t("paygap.statistical.indicators.men")}</TableCell>
                <TableCell align="right">
                  {analysisSummary.men.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  {t("paygap.statistical.indicators.excludedTraining")}
                </TableCell>
                <TableCell align="right">
                  {analysisSummary.excludedTraining.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  {t("paygap.statistical.indicators.zeroIncomeRecords")}
                </TableCell>
                <TableCell align="right">
                  {analysisSummary.zeroIncomeRecords.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  {t("paygap.statistical.indicators.invalidIncompleteRecords")}
                </TableCell>
                <TableCell align="right">
                  {analysisSummary.invalidIncompleteRecords.toLocaleString()}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  {t("paygap.statistical.indicators.degreesOfFreedom")}
                </TableCell>
                <TableCell align="right">
                  {analysisSummary.degreesOfFreedom.toLocaleString()}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default PaygapStatisticalIndicators;
